/**
 * Flickity Accessibility Fix
 * 
 * This script fixes accessibility issues with Flickity carousels by making
 * visually visible slides (is-previous, is-next) accessible to screen readers
 * while still being non-interactive.
 */
(function() {
    // Keep track of initialization state to avoid redundant processing
    let isInitialized = false;
    
    // Check if Flickity is available
    function isFlickityAvailable() {
      return typeof window.Flickity !== 'undefined' || 
             document.querySelector('.flickity-enabled') !== null;
    }
    
    // Main initialization function
    function init() {
      try {
        if (isInitialized) return;
        
        // Wait for Flickity to be available before initializing
        if (!isFlickityAvailable()) {
          // Retry after a short delay if Flickity isn't available yet
          setTimeout(init, 200);
          return;
        }
        
        // Fix on page load
        fixFlickityAccessibility();
        
        // Also fix whenever a carousel might be initialized or changed
        document.addEventListener('ready.flickity', handleFlickityEvent);
        document.addEventListener('change.flickity', handleFlickityEvent);
        
        // Add a mutation observer to watch for Flickity carousels dynamically added to the page
        setupMutationObserver();
        
        isInitialized = true;
        
        if (window.console && window.console.debug) {
          console.debug('Flickity Accessibility Fix initialized');
        }
      } catch (error) {
        // Log error but don't break the page
        if (window.console && window.console.error) {
          console.error('Error initializing Flickity Accessibility Fix:', error);
        }
      }
    }
    
    // Handler for Flickity events with error handling
    function handleFlickityEvent(event) {
      try {
        fixFlickityAccessibility();
      } catch (error) {
        if (window.console && window.console.error) {
          console.error('Error in Flickity event handler:', error);
        }
      }
    }
    
    // Setup mutation observer with proper scoping
    function setupMutationObserver() {
      try {
        // Find all carousel containers to observe instead of body
        const carouselContainers = document.querySelectorAll('.flickity-enabled, [data-flickity]');
        
        // Fallback to body if no containers found
        const targets = carouselContainers.length > 0 ? 
                       Array.from(carouselContainers) : 
                       [document.body];
        
        const observer = new MutationObserver(function(mutations) {
          let shouldFix = false;
          
          // Check if any mutations are relevant to carousels
          for (let i = 0; i < mutations.length; i++) {
            const mutation = mutations[i];
            
            // Check for class changes that might indicate Flickity state changes
            if (mutation.type === 'attributes' && 
                (mutation.attributeName === 'class' || 
                 mutation.attributeName === 'aria-hidden')) {
              shouldFix = true;
              break;
            }
            
            // Check for added nodes that might be new carousels
            if (mutation.type === 'childList' && mutation.addedNodes.length) {
              for (let j = 0; j < mutation.addedNodes.length; j++) {
                const node = mutation.addedNodes[j];
                if (node.nodeType === 1 && // Element node
                   (node.classList && 
                    (node.classList.contains('flickity-enabled') || 
                     node.hasAttribute('data-flickity') ||
                     node.querySelector('.flickity-enabled, [data-flickity]')))) {
                  shouldFix = true;
                  break;
                }
              }
            }
            
            if (shouldFix) break;
          }
          
          if (shouldFix) {
            fixFlickityAccessibility();
          }
        });
        
        // Observe each target
        targets.forEach(target => {
          observer.observe(target, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['class', 'aria-hidden']
          });
        });
      } catch (error) {
        if (window.console && window.console.error) {
          console.error('Error setting up mutation observer:', error);
        }
      }
    }
    
    /**
     * Fix Flickity accessibility by:
     * 1. Setting aria-hidden="false" on visually displayed slides (is-previous, is-next)
     * 2. Making them still non-interactive with tabindex=-1
     */
    function fixFlickityAccessibility() {
      try {
        // Get all Flickity carousels
        const carousels = document.querySelectorAll('.flickity-enabled');
        
        if (!carousels.length) return;
        
        carousels.forEach(carousel => {
          // Fix the previous and next slides
          const previousSlides = carousel.querySelectorAll('.is-previous[aria-hidden="true"]');
          const nextSlides = carousel.querySelectorAll('.is-next[aria-hidden="true"]');
          
          // Make visually visible slides accessible to screen readers
          [...previousSlides, ...nextSlides].forEach(slide => {
            slide.setAttribute('aria-hidden', 'false');
            
            // Make it non-interactive but accessible
            slide.setAttribute('tabindex', '-1');
            
            // Make child interactive elements non-interactive
            slide.querySelectorAll('a, button, input, select, textarea').forEach(element => {
              element.setAttribute('tabindex', '-1');
            });
          });
        });
      } catch (error) {
        if (window.console && window.console.error) {
          console.error('Error fixing Flickity accessibility:', error);
        }
      }
    }
    
    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
    
    // Try again when window loads, as a fallback
    window.addEventListener('load', function() {
      if (!isInitialized) {
        init();
      }
    });
  })(); 